// Global Instructions Rule Applied!
//
// Configuration → Deal Execution services: deal-type → execution-pipeline
// mapping and per-stage checklist items. Same fetch conventions as
// stageActionsConfigService.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  DealExecutionMappingRow,
  StageChecklistAdminRow,
  validateDealExecutionMappingRow,
  validateStageChecklistAdminRow
} from '../../../state_manager/data_models/deal_execution_admin_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    // The backend's error envelope key is `message` (handle_route_custom_error /
    // handle_route_error); `error` is the auth guard's. Same 401 wording as
    // pipelinesConfigService so every Configuration panel reads alike.
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchDealExecutionMapping = async (
  accessToken: string | null
): Promise<DealExecutionMappingRow[]> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DEAL_EXECUTION_MAPPING_LIST),
    { method: API_ROUTES.CONFIG.DEAL_EXECUTION_MAPPING_LIST.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const rows = (body.data || []) as DealExecutionMappingRow[]
  validateDealExecutionMappingRow(rows, { isArray: true })
  return rows
}

export const setDealExecutionMapping = async (
  accessToken: string | null,
  dealTypeId: string,
  pipelineId: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DEAL_EXECUTION_MAPPING_SET),
    {
      method: API_ROUTES.CONFIG.DEAL_EXECUTION_MAPPING_SET.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { deal_type_id: dealTypeId, pipeline_id: pipelineId } })
    }
  )
  await readJson(response)
}

export const fetchStageChecklistItems = async (
  accessToken: string | null
): Promise<StageChecklistAdminRow[]> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_CHECKLIST_LIST),
    { method: API_ROUTES.CONFIG.STAGE_CHECKLIST_LIST.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const rows = (body.data || []) as StageChecklistAdminRow[]
  validateStageChecklistAdminRow(rows, { isArray: true })
  return rows
}

export const createStageChecklistItem = async (
  accessToken: string | null,
  fields: { pipeline_config_id: string; label: string; sequence?: number }
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_CHECKLIST_CREATE),
    {
      method: API_ROUTES.CONFIG.STAGE_CHECKLIST_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateStageChecklistItem = async (
  accessToken: string | null,
  itemId: string,
  fields: { label?: string; sequence?: number; active?: boolean }
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_CHECKLIST_UPDATE, { itemId }),
    {
      method: API_ROUTES.CONFIG.STAGE_CHECKLIST_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteStageChecklistItem = async (
  accessToken: string | null,
  itemId: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_CHECKLIST_DELETE, { itemId }),
    { method: API_ROUTES.CONFIG.STAGE_CHECKLIST_DELETE.method, headers: authHeaders(accessToken) }
  )
  await readJson(response)
}
